<template>
  <div class="">
    <VasionTreeTable
      ref="portalFoldersTable"
      class="admin-portal-parent-folder-table"
      :columns="portalParentFolderTableColumns"
      :ellipsisOptions="ellipsisOptions"
      :ellipsisChildOptions="ellipsisChildOptions"
      :rows="portalFoldersList"
      :fetchChildren="fetchChildren"
      :childRowClick="childRowClick"
      :rowClick="editParentFolder"
    />
    <VasionGeneralModal
      id="portal-parent-folder-delete-modal"
      confirmButtonText="YES"
      rejectButtonText="NO"
      modalType="message"
      buttonGroup="center"
      :message="deleteMessage"
      :sync="showDeleteConfirmationModal"
      @confirmButtonClick="isSubfolderBeingDeleted ? deleteSubFolder() : deleteParentFolder()"
      @noButtonClick="showDeleteConfirmationModal = !showDeleteConfirmationModal"
    />
    <VasionSnackbar
      id="portal-parent-folder-snackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>

<script>
import VasionTreeTable from '@/components/shared/VasionTreeTable.vue'
import lists from './admin-folders-static-lists.json'

export default {
  name: 'PortalParentFolders',
  components: {
    VasionTreeTable,
  },
  data: function () {
    return {
      deleteMessage: 'Are you sure you want to delete this folder?',
      ellipsisOptions: [
        {
          label: 'Edit Folder',
          icon: 'VasionEditIcon',
          handler: this.editParentFolder,
        },
        {
          label: 'Refresh For Search',
          icon: 'VasionRefreshIcon',
          handler: this.refreshForSearch,
        },
        {
          label: 'Delete',
          icon: 'VasionDeleteIcon',
          handler: this.prepareDeleteAndShowModal,
        },
        {
          label: 'Create Subfolder',
          icon: 'VasionAddIcon',
          handler: this.prepareSubfolderCreation,
        },
      ],
      ellipsisChildOptions: [
        {
          label: 'Edit Subfolder',
          icon: 'VasionEditIcon',
          handler: this.childRowClick,
        },
        {
          label: 'Delete Subfolder',
          icon: 'VasionDeleteIcon',
          handler: this.prepareSubfolderDeleteAndShowModal,
        },
      ],
      folderDataPendingDeletion: {},
      isSubfolderBeingDeleted: false,
      portalParentFolderTableColumns: lists.portalParentFolderTableColumns,
      selectedFolder: null,
      showCreateSubFolderModal: false,
      showDeleteConfirmationModal: false,
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
    }
  },
  computed: {
    foldersList() {
      return this.$store.state.vault.vaultList
    },
    portalFoldersList() {
      return this.generatePortalFoldersList(this.foldersList)
    },
    subfolderNameError() {
      return this.subfolderNameTouched && this.subfolderName.trim() === ''
    },
  },
  methods: {
    childRowClick(row) {
      if (!row || !row.id) return

      const folderId = row.id
      const parentId = row.parentId
      this.$router.push({ 
        name: 'TheParentFolder', 
        params: { folderId: folderId },
        query: { 
          showOnlyFolderName: true,
          parentFolderId: parentId
        }
      })
    },
    async deleteParentFolder() {
      const folderId = this.folderDataPendingDeletion.id
      const response = await this.$store.dispatch('vault/deleteVault', folderId)
      this.showDeleteConfirmationModal = false

      if (response.Value === 'True') {
        this.showSnackbar('Folder Deleted Successfully', 'The Folder has been removed')
      } else {
        this.showSnackbar('Error Deleting Folder', `Error: ${response.ResultMessage}`, true)
      }

      await this.$store.dispatch('vault/getVaultList')
      this.$emit('refresh-table')
    },
    async deleteSubFolder() {
      const deleteFolderID = this.folderDataPendingDeletion.id
      this.showDeleteConfirmationModal = false

      const deleteSubFolder = await this.$store.dispatch('vault/deleteSubFolder', deleteFolderID)

      if (!deleteSubFolder) {
        this.showSnackbar('Error Deleting Subfolder', 'Could not delete the subfolder', true)
      } else if (deleteSubFolder.ResultStatus > 0) {
        this.showSnackbar('Subfolder Deleted Successfully', 'The subfolder has been removed')

        const parentId = this.folderDataPendingDeletion.parentId
        this.$refs.portalFoldersTable.updateChildren(parentId)
      }

      this.isSubfolderBeingDeleted = false
    },
    editParentFolder(row) {
      if (!row || !row.id) return

      const parentFolderId = row.id
      this.$router.push({ 
        name: 'TheParentFolder', 
        params: { folderId: parentFolderId },
        query: { isPortalFolder: true }
      })
    },
    async fetchChildren(parentId) {
      if (!parentId) return

      const parentFolderID = parentId
      const payload = {
        parentFolderId: parentFolderID,
        enforceUserSecurity: false,
        groupByStorageType: false,
      }
      const response = await this.$store.dispatch('common/getChildFolders', payload)

      let childrenFolders = []
      if (!response || !response.data || !response.data.FolderList) {
        this.showSnackbar('Error Fetching Folders', 'Could not fetch folders', true)
      } else if (
        response?.data &&
        response.data.FolderList &&
        response.data.FolderList.Values.length < 1
      ) {
        this.showSnackbar('No Folders Found', 'No folders found for this parent folder')
      } else if (
        response?.data &&
        response.data.FolderList &&
        response.data.FolderList.Values.length > 0
      ) {
        const parentFolder = this.portalFoldersList.find(pFolder => pFolder.id === parentId)
        childrenFolders = response.data.FolderList.Values.map(folder => {
          return {
            id: folder.FolderId || folder.Id,
            parentId: parentFolder.id,
            status: 'VasionFolderIcon',
            vaultName: folder.Name,
            storageDisplayName: parentFolder?.storageDisplayName,
            piiData: parentFolder?.piiData,
            readOnly: parentFolder?.readOnly,
            encrypted: parentFolder?.encrypted,
          }
        })
      }
      return childrenFolders
    },
    generatePortalFoldersList(foldersList) {
      if (!foldersList || foldersList.length === 0) return []

      const portalFolderType = 5
      const portalFolders = foldersList.filter(folder => folder.folderType === portalFolderType)

      return portalFolders.map(folder => {
        return {
          id: folder.vaultID,
          status: 'VasionFolderSparkIcon',
          vaultName: folder.vaultName,
          hasChildren: folder.hasChildren,
          storageDisplayName: folder.storageDisplayName,
          piiData: folder.piiData ? 'Yes' : 'No',
          readOnly: folder.readOnly ? 'Yes' : 'No',
          encrypted: folder.encrypted ? 'Yes' : 'No',
        }
      })
    },
    prepareDeleteAndShowModal(row) {
      if (!row || !row.id) return

      this.isSubfolderBeingDeleted = false
      this.folderDataPendingDeletion = row
      this.deleteMessage = `Are you sure you want to delete the Portal Parent Folder ${row.vaultName}?`
      this.showDeleteConfirmationModal = true
    },
    prepareSubfolderCreation(row) {
      if (!row || !row.id) return

      const parentFolderId = row.id
      this.$router.push({ 
        name: 'TheParentFolder', 
        params: { folderId: 0 },
        query: { 
          showOnlyFolderName: true,
          parentFolderId: parentFolderId,
          isSubfolder: true
        }
      })
    },
    prepareSubfolderDeleteAndShowModal(row) {
      if (!row || !row.id) return

      this.isSubfolderBeingDeleted = true
      this.folderDataPendingDeletion = row
      this.deleteMessage = `Are you sure you want to delete the Subfolder ${row.vaultName}?`
      this.showDeleteConfirmationModal = true
    },
    async refreshForSearch(row) {
      if (!row || !row.id) return

      const id = row.id
      const response = await this.$store.dispatch('search/updateFolderSearchIndex', id)

      if (response.ResultStatus > 0) {
        this.showSnackbar('Folder Refreshed', 'The Folder has been refreshed for searching')
      } else {
        this.showSnackbar(
          'Error Refreshing Folder',
          'Could not refresh the Folder for searching',
          true
        )
      }
    },
    showSnackbar(title, subTitle, isError = false) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: !isError,
        showSnackbar: true,
      }
    },
    handleSubfolderCreated() {
      this.showSnackbar('Subfolder Created', 'The subfolder has been created successfully')

      const parentId = this.selectedFolder.id
      this.$refs.portalFoldersTable.updateChildren(parentId)
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-portal-parent-folder-table {
  max-height: calc(100vh - 272px);
  overflow-y: auto;
}
</style>
